import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorUtils";
import * as actions from "../slices/fiscal";
import { mapFiscalInvoiceToSummary } from "../../mappers/fiscalMapper";

// ==========================================
// 1. DYNAMIC LOOKUP MATCHER
// ==========================================
export const fetchFiscalLookup = (key, endpoint) => async (dispatch) => {
  dispatch(actions.setLookupLoading(key));
  try {
    const { data } = await axiosInstance.get(`/fiscal/${endpoint}`, {
      headers: { requiresAuth: true },
    });
    if (data.status === "SUCCESS") {
      dispatch(actions.setLookupData({ key, data: data.data }));
    }
  } catch (error) {
    dispatch(actions.setLookupError({ key, error: getErrorMessage(error) }));
  }
};

// Convenience helpers for components
export const getCountries = () => fetchFiscalLookup("countries", "countries");
export const getCurrencies = () =>
  fetchFiscalLookup("currencies", "currencies");
export const getQuantityUnits = () =>
  fetchFiscalLookup("quantityUnits", "quantity-units");
export const getPackageUnits = () =>
  fetchFiscalLookup("packageUnits", "package-units");
export const getTaxGroups = () => fetchFiscalLookup("taxGroups", "tax-groups");
export const getPaymentModes = () =>
  fetchFiscalLookup("paymentModes", "payment-modes");
export const getSalesStatus = () =>
  fetchFiscalLookup("salesStatus", "sales-status");
export const getStockIoTypes = () =>
  fetchFiscalLookup("stockIoTypes", "stock-io-types");
export const getTransactionTypes = () =>
  fetchFiscalLookup("transactionTypes", "transaction-types");
export const getRefundReasons = () =>
  fetchFiscalLookup("refundReasons", "refund-reasons");
export const getNotices = () => fetchFiscalLookup("notices", "notices");
export const getBranches = () => fetchFiscalLookup("branches", "branches");

// ==========================================
// 2. ITEM CLASS CODES (Paginated)
// ==========================================
export const getItemClassCodes =
  ({ page = 1, limit = 100, search = "" } = {}) =>
  async (dispatch) => {
    dispatch(actions.setLoadingItemClassCodes());
    try {
      const params = { page, limit };
      if (search) params.search = search;

      const { data } = await axiosInstance.get("/fiscal/item-class-codes", {
        params,
        headers: { requiresAuth: true },
      });

      if (data.status === "SUCCESS") {
        dispatch(
          actions.setItemClassCodes({
            content: data.data.content,
            pagination: data.data.pagination,
          }),
        );
      }
    } catch (error) {
      dispatch(actions.setErrorItemClassCodes(getErrorMessage(error)));
    }
  };

// ==========================================
// 3. FISCAL ITEMS (Paginated)
// ==========================================
export const getFiscalItems =
  ({ page = 1, limit = 100, search = "" } = {}) =>
  async (dispatch) => {
    dispatch(actions.setLoadingFiscalItems());
    try {
      const params = { page, limit };
      if (search) params.search = search;

      const { data } = await axiosInstance.get("/fiscal/items", {
        params,
        headers: { requiresAuth: true },
      });

      if (data.status === "SUCCESS") {
        dispatch(
          actions.setFiscalItems({
            content: data.data.items,
            pagination: data.data.pagination,
          }),
        );
      }
    } catch (error) {
      dispatch(actions.setErrorFiscalItems(getErrorMessage(error)));
    }
  };

export const createFiscalItem = (values) => async (dispatch) => {
  dispatch(actions.setLoadingFiscalItemCreate());
  try {
    const { data } = await axiosInstance.post("/fiscal/items", values, {
      headers: { requiresAuth: true },
    });
    if (data.status === "SUCCESS") {
      toast.success(data.message || "Item pushed to eTIMS successfully!");
      dispatch(
        actions.setFiscalServerResponseStatus({
          operation: "create-item",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorFiscalItemCreate(msg));
  }
};

export const updateFiscalItem = (id, values) => async (dispatch) => {
  dispatch(actions.setLoadingFiscalItemUpdate());
  try {
    const { data } = await axiosInstance.put(`/fiscal/items/${id}`, values, {
      headers: { requiresAuth: true },
    });
    if (data.status === "SUCCESS") {
      toast.success(data.message || "eTIMS Item updated!");
      dispatch(
        actions.setFiscalServerResponseStatus({
          operation: "update-item",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorFiscalItemUpdate(msg));
  }
};

export const adjustFiscalStock = (values) => async (dispatch) => {
  dispatch(actions.setLoadingStockAdjust());
  try {
    const { data } = await axiosInstance.post(
      "/fiscal/items/adjust-stock",
      values,
      {
        headers: { requiresAuth: true },
      },
    );
    if (data.status === "SUCCESS") {
      toast.success(data.message || "Stock adjusted in eTIMS!");
      dispatch(
        actions.setFiscalServerResponseStatus({
          operation: "adjust-stock",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorStockAdjust(msg));
  }
};

// ==========================================
// 4. FISCAL INVOICES (Paginated)
// ==========================================
export const getFiscalInvoices =
  ({ startDate, endDate, page = 1, limit = 100 }) =>
  async (dispatch) => {
    dispatch(actions.setLoadingFiscalInvoices());
    try {
      const { data } = await axiosInstance.get("/fiscal/invoices", {
        params: { startDate, endDate, page, limit },
        headers: { requiresAuth: true },
      });
      if (data.status === "SUCCESS") {
        dispatch(
          actions.setFiscalInvoices({
            content: data.data.invoices,
            pagination: data.data.pagination,
          }),
        );
      }
    } catch (error) {
      dispatch(actions.setErrorFiscalInvoices(getErrorMessage(error)));
    }
  };

export const createFiscalInvoice = (values) => async (dispatch) => {
  dispatch(actions.setLoadingFiscalInvoiceCreate());
  try {
    const { data } = await axiosInstance.post("/fiscal/invoices", values, {
      headers: { requiresAuth: true },
    });
    if (data.status === "SUCCESS") {
      toast.success(data.message || "Invoice sent to eTIMS successfully!");
      const summary = mapFiscalInvoiceToSummary(data.data);
      dispatch(actions.createFiscalInvoiceSuccess(summary));
      dispatch(
        actions.setFiscalServerResponseStatus({
          operation: "create-invoice",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorFiscalInvoiceCreate(msg));
  }
};
export const getFiscalInvoiceById = (invoiceNumber) => async (dispatch) => {
  dispatch(actions.setLoadingFiscalInvoice());
  try {
    const { data } = await axiosInstance.get(
      `/fiscal/invoices/${invoiceNumber}`,
      {
        headers: { requiresAuth: true },
      },
    );
    if (data.status === "SUCCESS") {
      dispatch(actions.setCurrentFiscalInvoice(data.data));
    }
  } catch (error) {
    dispatch(actions.setErrorLoadingFiscalInvoice(getErrorMessage(error)));
  }
};

export const getFiscalInvoicePdf = (invoiceNumber) => async (dispatch) => {
  dispatch(actions.setLoadingInvoicePdf());
  try {
    const response = await axiosInstance.get(
      `/fiscal/invoices/pdf/${invoiceNumber}`,
      {
        headers: { requiresAuth: true },
        responseType: "blob",
      },
    );
    const pdfBlobUrl = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" }),
    );
    dispatch(actions.setPdfUrl(pdfBlobUrl));
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error("Failed to load PDF preview: " + msg);
    dispatch(actions.setErrorInvoicePdf(msg));
  }
};

export const emailFiscalInvoice =
  (invoiceNumber, email) => async (dispatch) => {
    dispatch(actions.setLoadingInvoiceEmail());
    try {
      const { data } = await axiosInstance.post(
        `/fiscal/invoices/email/${invoiceNumber}`,
        { email },
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === "SUCCESS") {
        toast.success(
          data.message || `Invoice queued for emailing to ${email}`,
        );
        dispatch(actions.setEmailSuccess());
        dispatch(
          actions.setFiscalServerResponseStatus({
            operation: "email-invoice",
            status: data.statusCode,
          }),
        );
      }
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(msg);
      dispatch(actions.setErrorInvoiceEmail(msg));
    }
  };

export const resetFiscalServerStatus = () => (dispatch) => {
  dispatch(actions.setFiscalServerResponseStatus(null));
};
